import { useVirtualizer } from '@tanstack/react-virtual';
import { type ReactNode, useEffect, useRef } from 'react';

/**
 * R14b (#3) — the ONE virtualization wrapper the queue lists share. Renders only
 * the rows in view (+ overscan) over a `@tanstack/react-virtual` virtualizer, so a
 * 281-invitation / thousands-of-thread queue stays smooth. Rows are dynamically
 * MEASURED (variable height — snippets wrap, badges wrap), and `onReachEnd` drives
 * infinite-query pagination as the operator scrolls (no "Load more" button).
 *
 * The `<ul>/<li>` semantics are preserved (the parent is the scroll element; each
 * row is an absolutely-positioned, measured `<li>`). Keyboard reach is via the
 * focusable row buttons (Tab → native focus-scroll), not the container, so it needs
 * no tabIndex; `scrollToIndex` scrolls the selected row into view when it has been
 * virtualized out of the DOM.
 */
export function VirtualList<T>({
  items,
  getKey,
  renderItem,
  estimateSize = 76,
  overscan = 8,
  onReachEnd,
  scrollToIndex,
  className,
  testId,
  ariaLabel,
}: {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
  estimateSize?: number;
  overscan?: number;
  /** Called once when the last row scrolls into view — wire to fetchNextPage. */
  onReachEnd?: () => void;
  /** Scroll this row index into view when it changes (keyboard nav / selection). */
  scrollToIndex?: number | null;
  className?: string;
  testId?: string;
  ariaLabel?: string;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan,
    getItemKey: (index) => {
      const item = items[index];
      return item === undefined ? index : getKey(item, index);
    },
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastIndex = virtualItems.length > 0 ? virtualItems[virtualItems.length - 1]?.index : null;

  // Infinite scroll: fire onReachEnd ONCE per items.length boundary when the final
  // row is in view. Tracking the length we fired at stops a re-fire on every poll /
  // SSE invalidation (which re-render this component without changing the list).
  const firedAtLengthRef = useRef(-1);
  useEffect(() => {
    if (!onReachEnd) return;
    if (lastIndex != null && lastIndex >= items.length - 1) {
      if (firedAtLengthRef.current !== items.length) {
        firedAtLengthRef.current = items.length;
        onReachEnd();
      }
    } else {
      firedAtLengthRef.current = -1; // scrolled away → allow re-fire at the end
    }
  }, [onReachEnd, lastIndex, items.length]);

  // Scroll a newly-selected row into view (it may be outside the virtual window).
  // biome-ignore lint/correctness/useExhaustiveDependencies: virtualizer is a stable instance
  useEffect(() => {
    if (scrollToIndex != null && scrollToIndex >= 0) {
      virtualizer.scrollToIndex(scrollToIndex, { align: 'auto' });
    }
  }, [scrollToIndex]);

  return (
    // Keyboard reach is via the focusable row buttons (Tab focuses a row → native
    // focus-scroll → onReachEnd pages) + scrollToIndex on selection nav (#10), so
    // the scroll container itself needs no tabIndex.
    <div ref={parentRef} className={className} data-testid={testId}>
      <ul
        aria-label={ariaLabel}
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
          margin: 0,
          // Horizontal-only inset (vertical padding would desync the absolute
          // row offsets measured from the scroll element). R14b review #11.
          padding: '0 0.35rem',
          listStyle: 'none',
        }}
      >
        {virtualItems.map((vi) => {
          const item = items[vi.index];
          if (item === undefined) return null;
          return (
            <li
              key={vi.key}
              data-index={vi.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vi.start}px)`,
              }}
            >
              {renderItem(item, vi.index)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
